import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LifeBuoy, X, Send, Loader2 } from 'lucide-react';
import { postSupportMessage, getSupportThread } from '@/api/supportWidgetApi';
import { useSTNAuth } from '@/context/STNAuthContext';

// Global support widget (Phase 2 of the Master Development Roadmap) — founder
// side. Same backend and component pattern as SkillNexus/InternNexus/
// MentorNexus's SupportWidget.jsx. Founder identity isn't auto-resolved
// server-side (see supportWidgetApi.js's header comment), so name/email are
// pulled from useSTNAuth() and sent explicitly on every call.
const STATUS_LABEL = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };

export default function SupportWidget() {
  const { founder } = useSTNAuth();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && !loaded && founder?.email) {
      getSupportThread(founder.email)
        .then((data) => {
          setTicket(data?.ticket || null);
          setMessages(data?.messages || []);
        })
        .catch(() => {})
        .finally(() => {
          setLoaded(true);
          setTimeout(() => inputRef.current?.focus(), 150);
        });
    }
  }, [open, loaded, founder?.email]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending || !founder?.email) return;
    setInput('');
    setMessages((prev) => [...prev, { direction: 'inbound', body: text, createdAt: new Date().toISOString() }]);
    setSending(true);
    try {
      await postSupportMessage(text, founder.name, founder.email);
    } catch (err) {
      setMessages((prev) => [...prev, { direction: 'outbound', body: err.message || 'Failed to send — please try again.', createdAt: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  if (!founder) return null;

  return (
    <div className="fixed right-4 sm:right-6 bottom-4 sm:bottom-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-[min(360px,calc(100vw-2rem))] rounded-2xl border border-white/10 bg-[#0F172A] overflow-hidden flex flex-col shadow-2xl"
            style={{ maxHeight: '500px' }}
          >
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/10">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-primary">
                <LifeBuoy className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white leading-tight">Support</p>
                <p className="text-[10px] text-white/60">
                  {ticket ? `${STATUS_LABEL[ticket.status] || ticket.status} · #${String(ticket._id).slice(-6)}` : 'We usually reply within a day'}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ minHeight: 0, maxHeight: '360px' }}>
              {!loaded && (
                <div className="flex items-center justify-center py-8 text-white/60 text-xs gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                </div>
              )}
              {loaded && messages.length === 0 && (
                <p className="text-xs text-white/60 text-center py-6">
                  Send us a message and our support team will get back to you here.
                </p>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.direction === 'inbound' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] rounded-xl px-3 py-2.5 text-xs leading-relaxed ${
                      m.direction === 'inbound'
                        ? 'bg-sky-400/10 border border-sky-400/20 text-white'
                        : 'bg-muted border border-white/10 text-white'
                    }`}
                  >
                    {m.body}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="px-3 pb-3 pt-2 border-t border-white/10">
              <div className="relative flex items-center gap-2 bg-muted border border-white/10 rounded-xl px-3 py-2 focus-within:border-sky-400/40 transition-colors">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
                  placeholder="Type your message..."
                  className="flex-1 bg-transparent text-xs text-white placeholder:text-white/60 focus:outline-none"
                />
                <button
                  onClick={send}
                  disabled={!input.trim() || sending}
                  className="w-6 h-6 rounded-lg flex items-center justify-center bg-primary disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  {sending ? <Loader2 className="w-3 h-3 animate-spin text-primary-foreground" /> : <Send className="w-3 h-3 text-primary-foreground" />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="relative w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-primary"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X className="w-5 h-5 text-primary-foreground" />
            </motion.div>
          ) : (
            <motion.div key="help" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <LifeBuoy className="w-6 h-6 text-primary-foreground" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
